import {
  Badge,
  ButtonIcon,
  ButtonText,
  OutlineCard,
  Select,
  Table,
  useToast
} from '@morressier/alembic';
import { Box, Flex, Text, Divider as ChakraDivider } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDownToBracket,
  faCopy,
  faPenToSquare,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { useSubmissionData } from '../../contexts/SubmissionDataProvider';
import {
  fetchOverviewDetails,
  fetchSubmissionIntegrityReports
} from 'src/api/queries';
import RecommendationBadge from '../RecommendationBadge';
import Divider from '../Divider';
import { DecisionHistory } from './DecisionHistory';
import { SubmissionFilesAccordion } from './SubmissionFilesAccordion';
import { NotesAccordion } from './NotesAccordion';
import TransferHistoryAccordion from './TransferHistoryAccordion';
import { useQuery } from '@tanstack/react-query';
import dateFormat from 'dateformat';
import { useEffect, useState } from 'react';
import Loading from 'src/pages/Status/Loading';
import { OverwriteReviewsModal } from './AdditionalInformation/editorial/common/OverwriteReviewsModal';
import { decisionStatusBadges, recommendationBadges } from '../Review/Badges';
import PeerReviewFullView, {
  ParticipantReviewerName
} from './PeerReviewFullView';
import DecisionHistoryFullView from './DecisionHistoryFullView';
import { IRoleType, ISubmissionFile } from '@publishing-engine/domain/models';
import ShowAttachementFiles from './ShowAttachementFiles';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import ProtectedWorkflowAction from 'src/components/ProtectedAction/ProtectedWorkflowAction';

export const DividerVertical = () => (
  <Box
    height="20px"
    borderRight="1px solid"
    borderColor="neutralPrimaryBorder"
    mx={2}
  />
);
export const cocUnassignStates = [
  'peerReviewPending',
  'peerReviewInvitation',
  'peerReviewFinished',
  'revisionAssessment',
  'revisionPeerReviewInvitation',
  'revisionPeerReviewPending',
  'revisionPeerReviewFinished'
];
const OverviewTab: React.FC<{ roleContext: IRoleType }> = ({ roleContext }) => {
  const {
    submissionId,
    submissionContextId,
    workflowResponse: { manuscriptId, workflowData },
    refetchSubmission,
    organizationId
  } = useSubmissionData();
  const toast = useToast();
  const submissionCurrentState =
    workflowData.currentState as ISubmissionWFState;
  const [roundSelection, setRoundSelection] = useState<{
    submissionId: string;
    round: string;
  }>({
    submissionId,
    round: manuscriptId ?? ''
  });
  const selectedRound = roundSelection.round;

  useEffect(() => {
    setRoundSelection({
      submissionId,
      round: manuscriptId ?? ''
    });
  }, [submissionId, manuscriptId]);

  const [showAttachments, setShowAttachments] = useState<
    ISubmissionFile[] | null
  >(null);
  const {
    data: overviewDetails,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['overview-details', { submissionId, selectedRound }],
    queryFn: () => fetchOverviewDetails(submissionId, selectedRound),
    enabled:
      !!submissionId &&
      !!selectedRound &&
      roundSelection.submissionId === submissionId,
    retry: 0
  });

  //transfer history :
  const { data: pastJournalsHistoryData } = useQuery({
    queryKey: ['submission-integrity', { submissionId }],
    queryFn: () => fetchSubmissionIntegrityReports(submissionId),
    retry: 0
  });
  const { submissionContext } = useSubmissionContextData();
  const [showFullView, setShowFullView] = useState({ status: false, card: '' });
  const [minReviewsModalOpen, setMinReviewsModalOpen] = useState(false);
  const hasCOCEnabled = submissionContext?.config?.isCOCRecommendationRequired;
  if (!overviewDetails || isLoading) return <Loading />;
  if (isError) {
    return (
      <SubmissionLoader
        title="Search Failed"
        subTitle="We encountered an issue connecting to the search service. Please check your connection or try again later."
        bgColor="errorPrimaryFill"
      />
    );
  }
  const round = overviewDetails.overviewRevisions.filter(
    item => item.manuscriptId === selectedRound
  )[0];
  const isTargetAllowed = cocUnassignStates.includes(submissionCurrentState);
  const overviewDetailsData = round?.overviewDetails;
  const decision = overviewDetailsData?.submissionDecisionDetails?.decision;
  const badgeKey =
    decision?.type === 'RevisionRequested' &&
    decision?.formPayload?.revisionSeverity
      ? `${decision.formPayload.revisionSeverity}Revision`
      : decision?.type ?? '';
  const filteredPeerReview =
    overviewDetailsData?.peerReviewDetails?.participants ?? [];
  const handleCloseModal = () => {
    setMinReviewsModalOpen(false);
  };
  const scrollToNotes = () => {
    document.getElementById('notes')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const handleCopy = async (variable: string) => {
    await navigator.clipboard.writeText(variable);
    toast({
      variant: 'success',
      title: 'Copied to clipboard',
      description: '',
      isClosable: true
    });
  };

  return (
    <>
      {minReviewsModalOpen && (
        <OverwriteReviewsModal
          handleCloseModal={handleCloseModal}
          defaultMinReviews={
            overviewDetailsData?.peerReviewDetails.peerReviewStats
              .reviewTarget ?? 0
          }
          refetchSubmission={refetchSubmission}
          submissionContextId={submissionContextId}
          organizationId={organizationId}
          submissionId={submissionId}
        />
      )}
      {showAttachments && (
        <ShowAttachementFiles
          attachments={showAttachments}
          onClose={() => setShowAttachments(null)}
        />
      )}
      <Flex
        align={{ base: 'flex-start', md: 'center' }}
        gap={2}
        justify="space-between"
        flexDirection={{ base: 'column', md: 'row' }}
      >
        <Flex justify="space-between" align="flex-start" gap={6} wrap="wrap">
          <Text textStyle="headingItem">
            {`${round?.manuscriptId} | ${
              round?.isOriginalSubmission
                ? 'Original Submission'
                : `Revision - ${round?.revisionNumber}`
            }`}
            <br />
            {'('}
            {dateFormat(
              overviewDetailsData?.submissionDecisionDetails?.dateEntered,
              'mmm d, yyyy'
            )}
            {' to '}
            {overviewDetailsData?.submissionDecisionDetails?.dateCompleted
              ? dateFormat(
                  overviewDetailsData?.submissionDecisionDetails?.dateCompleted,
                  'mmm d, yyyy'
                )
              : 'Ongoing'}
            {')'}
          </Text>

          {round?.isCurrentRound && (
            <Badge size="context" variant="neutral">
              Current Round
            </Badge>
          )}
        </Flex>
        <Select
          width={{ base: '100%', md: '220px' }}
          value={selectedRound}
          onChange={e =>
            setRoundSelection(prev => ({ ...prev, round: e.target.value }))
          }
          justifyContent="flex-end"
        >
          {overviewDetails?.overviewRevisions.map(r => (
            <option key={r.manuscriptId} value={r.manuscriptId}>
              {r.isOriginalSubmission
                ? 'Original Submission'
                : `Revision - ${r.revisionNumber}`}
            </option>
          ))}
        </Select>
      </Flex>

      <Flex align="center" mb={2} gap={2}>
        <Text textStyle="textBody">Decision:</Text>

        {!overviewDetailsData?.submissionDecisionDetails?.decision ? (
          <Text textStyle="textContext" color="gray.500">
            No decision made yet
          </Text>
        ) : (
          <Text textStyle="textContext" textTransform="capitalize">
            {decisionStatusBadges[badgeKey] || '--'}
          </Text>
        )}
      </Flex>
      <Flex gap={1} align="center" wrap="wrap">
        {hasCOCEnabled && (
          <Flex
            gap={2}
            align={{ base: 'flex-start', md: 'center' }}
            flexDirection={{ base: 'column', md: 'row' }}
          >
            {/* Last Recommendation */}
            <Flex align="center" gap={2}>
              <Text textStyle="textBody" whiteSpace="nowrap">
                Last recommendation:
              </Text>

              {overviewDetailsData?.cocRecommendationDetails
                ?.lastRecommendationOrDecision ? (
                <>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    {
                      overviewDetailsData?.cocRecommendationDetails
                        ?.lastRecommendationOrDecisionRole
                    }
                  </Text>
                  <RecommendationBadge
                    recommendation={
                      overviewDetailsData?.cocRecommendationDetails
                        ?.lastRecommendationOrDecision
                    }
                  />
                </>
              ) : (
                <Text textStyle="textContext" color="gray.500">
                  No recommendation yet
                </Text>
              )}
            </Flex>

            {/* Recommendation Count */}
            <>
              <Box display={{ base: 'none', md: 'block' }}>
                <DividerVertical />
              </Box>
              <Flex align="center" gap={1}>
                <Text textStyle="textBody" isTruncated>
                  Recommendations:
                </Text>
                <ButtonText
                  isDisabled={
                    (overviewDetailsData?.cocRecommendationDetails
                      ?.recommendationsCount ?? 0) === 0
                  }
                  onClick={() =>
                    setShowFullView({
                      status: true,
                      card: 'RecommendationsOrDecisions'
                    })
                  }
                  minW={0}
                >
                  {overviewDetailsData?.cocRecommendationDetails
                    ?.recommendationsCount ?? 0}
                </ButtonText>
              </Flex>
              <Box display={{ base: 'none', md: 'block' }}>
                <DividerVertical />
              </Box>
            </>
          </Flex>
        )}

        <Flex align="center" gap={1}>
          <Text textStyle="textBody">Decision history:</Text>
          <ButtonText
            isDisabled={overviewDetailsData?.decisionHistoryCount === 0}
            onClick={() =>
              setShowFullView({
                status: true,
                card: 'DecisionHistory'
              })
            }
            minW={0}
          >
            {overviewDetailsData?.decisionHistoryCount}
          </ButtonText>
        </Flex>
        <DividerVertical />
        {/* Notes */}
        <Flex align="center" gap={1}>
          <Text textStyle="textBody">Notes:</Text>
          <ButtonText
            onClick={scrollToNotes}
            isDisabled={overviewDetailsData?.notesCount === 0}
            minW={0}
          >
            {overviewDetailsData?.notesCount ?? 0}
          </ButtonText>
        </Flex>

        {/* Peer Reviews */}
        <Flex align="center" gap={1} flexBasis="100%">
          {/* Target */}
          <Flex align="center" gap={1}>
            <Text textStyle="textBody">Peer Reviews Target:</Text>
            <ProtectedWorkflowAction actionName="SetMinReviewCount">
              <ButtonText
                onClick={() => setMinReviewsModalOpen(true)}
                iconRight={<FontAwesomeIcon icon={faPenToSquare} />}
                disabled={!isTargetAllowed || !round?.isCurrentRound}
              >
                {
                  overviewDetailsData?.peerReviewDetails.peerReviewStats
                    .reviewTarget
                }
              </ButtonText>
            </ProtectedWorkflowAction>
          </Flex>

          <DividerVertical />

          {/* Invited */}
          <Flex align="center" gap={1}>
            <Text textStyle="textBody">Invited:</Text>
            <Text textStyle="textBody">
              {overviewDetailsData?.peerReviewDetails.peerReviewStats.invited}
            </Text>
          </Flex>

          <DividerVertical />

          {/* Agreed */}
          <Flex align="center" gap={1}>
            <Text textStyle="textBody">Agreed:</Text>
            <Text textStyle="textBody">
              {overviewDetailsData?.peerReviewDetails.peerReviewStats.agreed ??
                0}
            </Text>
          </Flex>

          <DividerVertical />

          {/* Declined */}
          <Flex align="center" gap={1}>
            <Text textStyle="textBody">Declined:</Text>
            <Text textStyle="textBody">
              {overviewDetailsData?.peerReviewDetails.peerReviewStats
                .declined ?? 0}
            </Text>
          </Flex>

          <DividerVertical />

          {/* No Response */}
          <Flex align="center" gap={1}>
            <Text textStyle="textBody">No response:</Text>
            <Text textStyle="textBody">
              {overviewDetailsData?.peerReviewDetails.peerReviewStats
                .noResponse ?? 0}
            </Text>
          </Flex>

          <DividerVertical />

          {/* Completed */}
          <Flex align="center" gap={1}>
            <Text textStyle="textBody">Completed:</Text>
            <Text textStyle="textBody">
              {overviewDetailsData?.peerReviewDetails.peerReviewStats
                .completed ?? 0}
            </Text>
          </Flex>
        </Flex>
        {overviewDetailsData &&
          Object.keys(
            overviewDetailsData.peerReviewDetails.peerReviewStats
              .reviewRecommendations
          ).length !== 0 && (
            <Flex align="center" gap={2} wrap="wrap" flexBasis="100%">
              <Text textStyle="textBody">Peer Review(s):</Text>

              {Object.entries(
                overviewDetailsData.peerReviewDetails.peerReviewStats
                  .reviewRecommendations
              ).map(([key, value], index) => (
                <ButtonText
                  key={key}
                  onClick={() =>
                    setShowFullView({
                      status: true,
                      card: 'PeerReviews'
                    })
                  }
                  minW={0}
                >
                  {value}{' '}
                  {key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, c => c.toUpperCase())}
                  {Object.keys(
                    overviewDetailsData.peerReviewDetails.peerReviewStats
                      .reviewRecommendations
                  ).length -
                    1 !==
                    index && ','}
                </ButtonText>
              ))}
            </Flex>
          )}
      </Flex>
      <Flex flexDirection="column" gap={4} marginTop={4}>
        {showFullView.status ? (
          <>
            <OutlineCard width={'100%'} padding={4}>
              <Flex justifyContent="space-between">
                {showFullView.card === 'RecommendationsOrDecisions' && (
                  <Text textStyle="headingLine">
                    Recommendations (
                    {
                      overviewDetailsData?.cocRecommendationDetails
                        .recommendationsCount
                    }
                    )
                  </Text>
                )}
                {showFullView.card === 'PeerReviews' && (
                  <Text textStyle="headingLine">
                    Peer Review ({filteredPeerReview.length})
                  </Text>
                )}
                {showFullView.card === 'DecisionHistory' && (
                  <Text textStyle="headingLine">
                    Decision History (
                    {overviewDetailsData?.decisionHistoryCount ?? 0})
                  </Text>
                )}
                <ButtonText
                  onClick={() => setShowFullView({ status: false, card: '' })}
                  rightIcon={<FontAwesomeIcon icon={faXmark} />}
                >
                  Close
                </ButtonText>
              </Flex>
              {showFullView.card === 'RecommendationsOrDecisions' &&
                overviewDetailsData &&
                overviewDetailsData?.cocRecommendationDetails.cycles.length >
                  0 &&
                overviewDetailsData?.cocRecommendationDetails.cycles.map(
                  cycle => (
                    <OutlineCard mt={4}>
                      {cycle.participants.map((participant, index) => (
                        <>
                          <Flex
                            justifyContent="space-between"
                            padding={2}
                            gap={3}
                            flexWrap="wrap"
                          >
                            <Flex gap={3} alignItems="center">
                              <Text textStyle="headingLine">
                                {participant.role}
                              </Text>
                              <Text textStyle="textBody">
                                {participant.userFirstName}{' '}
                                {participant.userLastName}
                              </Text>
                            </Flex>
                            {participant.recommendation ? (
                              <>
                                <Box>
                                  <RecommendationBadge
                                    recommendation={
                                      participant.recommendation.decision ?? ''
                                    }
                                  />
                                </Box>
                                <Text textStyle="textContext">
                                  {dateFormat(
                                    participant.completedAt,
                                    'mmm d, yyyy'
                                  )}
                                </Text>
                                <ButtonText
                                  rightIcon={
                                    <FontAwesomeIcon
                                      icon={faArrowDownToBracket}
                                    />
                                  }
                                  onClick={() =>
                                    setShowAttachments(
                                      participant.recommendation?.attachments ||
                                        []
                                    )
                                  }
                                  disabled={
                                    participant.recommendation.attachments
                                      ?.length === 0
                                  }
                                >
                                  {participant.recommendation.attachments
                                    ?.length ?? 0}{' '}
                                  Files
                                </ButtonText>
                              </>
                            ) : (
                              <>
                                {participant.status === 'awaiting' && (
                                  <Text
                                    textStyle="textContext"
                                    color="gray.500"
                                  >
                                    Awaiting Recommendation
                                  </Text>
                                )}
                                {participant.status === 'interrupted' && (
                                  <Text
                                    textStyle="textContext"
                                    color="gray.500"
                                  >
                                    Interrupted
                                  </Text>
                                )}
                              </>
                            )}
                          </Flex>
                          {participant.recommendation && (
                            <Flex padding={2}>
                              <Flex flexDirection="column" gap={1}>
                                <Flex gap={2} alignItems="center">
                                  <Text textStyle="headingLine">
                                    Comment for Author(s)
                                  </Text>
                                  <ButtonIcon
                                    ariaLabel="Copy"
                                    icon={<FontAwesomeIcon icon={faCopy} />}
                                    variant="primary"
                                    onClick={() =>
                                      handleCopy(
                                        participant.recommendation
                                          ?.commentForAuthor ?? ''
                                      )
                                    }
                                    disabled={
                                      !participant.recommendation
                                        ?.commentForAuthor
                                    }
                                  />
                                </Flex>
                                <Text>
                                  {participant.recommendation
                                    ?.commentForAuthor || '--'}
                                </Text>
                                <Text textStyle="headingLine">
                                  Comment for Staff
                                </Text>
                                <Text>
                                  {participant.recommendation
                                    ?.commentForEditor || '--'}
                                </Text>
                              </Flex>
                            </Flex>
                          )}
                          {cycle.participants.length - 1 !== index && (
                            <>{Divider}</>
                          )}
                        </>
                      ))}
                    </OutlineCard>
                  )
                )}

              {showFullView.card === 'PeerReviews' && (
                <>
                  <Box mt={4}>{Divider}</Box>
                  <PeerReviewFullView participants={filteredPeerReview} />
                </>
              )}
              {showFullView.card === 'DecisionHistory' && (
                <>
                  <Box mt={4}>{Divider}</Box>
                  {overviewDetailsData?.decisionHistory && (
                    <DecisionHistoryFullView
                      decisionHistory={overviewDetailsData.decisionHistory}
                      roleContext={roleContext}
                    />
                  )}
                </>
              )}
            </OutlineCard>
          </>
        ) : (
          <>
            <Flex
              justifyContent="space-between"
              gap={2}
              flexDirection={{ base: 'column', md: 'row' }}
            >
              {hasCOCEnabled && (
                <Box width={{ base: '100%', md: '50%' }}>
                  <OutlineCard
                    width="100%"
                    onClick={() => {
                      if (
                        overviewDetailsData &&
                        overviewDetailsData.cocRecommendationDetails.cycles
                          .length > 0
                      ) {
                        setShowFullView({
                          status: true,
                          card: 'RecommendationsOrDecisions'
                        });
                      }
                    }}
                    _hover={{ background: 'aliceblue', cursor: 'pointer' }}
                  >
                    <Text textStyle="headingItem" padding={4}>
                      Recommendations (
                      {
                        overviewDetailsData?.cocRecommendationDetails
                          .recommendationsCount
                      }
                      )
                    </Text>
                    {Divider}
                    {overviewDetailsData &&
                    overviewDetailsData.cocRecommendationDetails.cycles
                      .length === 0 ? (
                      <Flex
                        justifyContent="flex-start"
                        alignItems="center"
                        padding={4}
                        minH={'100px'}
                      >
                        <Text textStyle="textContext">
                          No recommendations have been made yet.
                        </Text>
                      </Flex>
                    ) : (
                      <Flex
                        gap={2}
                        flexDirection="column"
                        padding={2}
                        maxH="250px"
                        overflowY="auto"
                      >
                        {overviewDetailsData?.cocRecommendationDetails.cycles.map(
                          cycle => (
                            <OutlineCard>
                              {cycle.participants.map((participant, index) => (
                                <Flex
                                  padding={2}
                                  gap={1}
                                  flexDirection={'column'}
                                >
                                  <Text textStyle="textBody">
                                    {participant.role}:{' '}
                                    {participant.userFirstName}{' '}
                                    {participant.userLastName}
                                  </Text>
                                  {participant.recommendation ? (
                                    <>
                                      <Box
                                        display={'flex'}
                                        gap={2}
                                        alignItems="center"
                                      >
                                        <RecommendationBadge
                                          recommendation={
                                            participant.recommendation
                                              .decision ?? ''
                                          }
                                        />
                                        <Text textStyle="textContext">
                                          Submitted on{' '}
                                          {dateFormat(
                                            participant.completedAt,
                                            'mmm d, yyyy'
                                          )}
                                        </Text>
                                      </Box>
                                    </>
                                  ) : (
                                    <>
                                      {participant.status === 'awaiting' && (
                                        <Text
                                          textStyle="textContext"
                                          color="gray.500"
                                        >
                                          Awaiting Recommendation
                                        </Text>
                                      )}
                                      {participant.status === 'interrupted' && (
                                        <Text
                                          textStyle="textContext"
                                          color="gray.500"
                                        >
                                          Interrupted
                                        </Text>
                                      )}
                                    </>
                                  )}
                                  {cycle.participants.length - 1 !== index && (
                                    <>{Divider}</>
                                  )}
                                </Flex>
                              ))}
                            </OutlineCard>
                          )
                        )}
                      </Flex>
                    )}
                  </OutlineCard>
                </Box>
              )}
              <Flex
                width={{ base: '100%', md: hasCOCEnabled ? '50%' : '100%' }}
              >
                <OutlineCard
                  width="100%"
                  onClick={() => {
                    if (filteredPeerReview.length > 0)
                      setShowFullView({
                        status: true,
                        card: 'PeerReviews'
                      });
                  }}
                  _hover={{ cursor: 'pointer', background: 'aliceblue' }}
                  pb={!hasCOCEnabled ? 2 : undefined}
                >
                  <Text textStyle="headingItem" padding={4}>
                    Peer Reviews ({filteredPeerReview?.length ?? 0})
                  </Text>
                  {Divider}
                  {filteredPeerReview && filteredPeerReview.length > 0 ? (
                    !hasCOCEnabled ? (
                      <Table>
                        <Table.HeaderRow
                          display="flex"
                          alignItems="center"
                          backgroundColor="surfaceSecondaryFill"
                        >
                          <Table.HeaderCell flex={0.4} paddingX={2}>
                            S.No.
                          </Table.HeaderCell>
                          <Table.HeaderCell flex={1} paddingX={2}>
                            Name
                          </Table.HeaderCell>
                          <Table.HeaderCell flex={1} paddingX={2}>
                            Recommendation
                          </Table.HeaderCell>
                          <Table.HeaderCell flex={1} paddingX={2}>
                            Submitted on
                          </Table.HeaderCell>
                        </Table.HeaderRow>
                        <Table.Body>
                          {filteredPeerReview.map((participant, index) => (
                            <Table.Row
                              key={index}
                              alignItems="center"
                              _last={{ borderBottom: 'none' }}
                            >
                              <Table.RowCell flex={0.4} paddingX={2}>
                                {index + 1}
                              </Table.RowCell>
                              <Table.RowCell flex={1} paddingX={2}>
                                <ParticipantReviewerName
                                  participant={participant}
                                  index={index}
                                />
                              </Table.RowCell>
                              <Table.RowCell flex={1} paddingX={2}>
                                {
                                  recommendationBadges[
                                    participant.reviewerRecommendationData
                                      ?.recommendation ?? ''
                                  ]
                                }
                              </Table.RowCell>
                              <Table.RowCell flex={1} paddingX={2}>
                                <Text textStyle="textContext">
                                  {dateFormat(
                                    participant.reviewerRecommendationData
                                      ?.date,
                                    'mmm d, yyyy'
                                  )}
                                </Text>
                              </Table.RowCell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    ) : (
                      <OutlineCard
                        padding={2}
                        margin={2}
                        maxH="250px"
                        overflowY="auto"
                      >
                        {filteredPeerReview.map((participant, index) => (
                          <Flex
                            key={index}
                            padding={2}
                            gap={1}
                            flexDirection="column"
                          >
                            <ParticipantReviewerName
                              participant={participant}
                              index={index}
                            />
                            <Box display="flex" gap={2} alignItems="center">
                              {
                                recommendationBadges[
                                  participant.reviewerRecommendationData
                                    ?.recommendation ?? ''
                                ]
                              }
                              <Text textStyle="textContext">
                                Submitted on{' '}
                                {dateFormat(
                                  participant.reviewerRecommendationData?.date,
                                  'mmm d, yyyy'
                                )}
                              </Text>
                            </Box>
                            {filteredPeerReview.length - 1 !== index && Divider}
                          </Flex>
                        ))}
                      </OutlineCard>
                    )
                  ) : (
                    <Flex
                      justifyContent="flex-start"
                      alignItems="center"
                      padding={4}
                      minH={'100px'}
                    >
                      <Text textStyle="textContext">
                        No peer review has been made yet.
                      </Text>
                    </Flex>
                  )}
                </OutlineCard>
              </Flex>
            </Flex>
            {overviewDetailsData?.decisionHistory && (
              <DecisionHistory
                decisionHistoryCount={
                  overviewDetailsData?.decisionHistoryCount ?? 0
                }
                submissionContextId={submissionContextId}
                decisionHistory={overviewDetailsData.decisionHistory}
                roleContext={roleContext}
                onClick={() =>
                  setShowFullView({
                    status: true,
                    card: 'DecisionHistory'
                  })
                }
              />
            )}
            {overviewDetailsData?.submissionFileDetails.fileSet && (
              <SubmissionFilesAccordion
                fileset={overviewDetailsData.submissionFileDetails.fileSet}
                totalFileCount={
                  overviewDetailsData?.submissionFileDetails.totalFileCount ?? 0
                }
                submissionContextId={submissionContextId}
              />
            )}
            <NotesAccordion
              totalNotesCount={overviewDetailsData?.notesCount ?? 0}
              notes={overviewDetailsData?.notes}
            />
            {pastJournalsHistoryData?.pastJournalHistory &&
              pastJournalsHistoryData.pastJournalHistory.length > 0 && (
                <>
                  <ChakraDivider
                    orientation="horizontal"
                    flex="1"
                    mt={4}
                    borderColor="gray.300"
                  />
                  <Box>
                    <Text textStyle="headingGroup" mb={3}>
                      Transfer History (
                      {pastJournalsHistoryData.pastJournalHistory.length})
                    </Text>
                    <TransferHistoryAccordion
                      transferHistory={
                        pastJournalsHistoryData.pastJournalHistory
                      }
                      submissionId={submissionId}
                      submissionContextId={submissionContextId}
                      roleContext={roleContext}
                    />
                  </Box>
                </>
              )}
          </>
        )}
      </Flex>
    </>
  );
};

export default OverviewTab;
